<template>
  <UikitListItem
    class="osk-products-filters-item-multilist"
    no-padding
  >
    <div
      :class="{
        'osk-products-filters-item-multilist__top': true,
        'osk-products-filters-item-multilist__top_hover': item.hasMoreValues,
      }"
      @click="handleClick"
    >
      <span>
        {{ item.name }}
      </span>

      <UikitTooltip
        v-if="item.descriptions"
        :title="item.name"
        @open="handleTooltipOpen"
      >
        <div
          v-for="(desc, index) in item.descriptions"
          :key="index"
          class="osk-products-filters-item-multilist__tooltip-list"
        >
          <div
            v-if="desc.name"
            class="osk-products-filters-item-multilist__tooltip-name"
          >
            {{ desc.name }}
          </div>

          <div
            v-if="desc.description"
            class="osk-products-filters-item-multilist__tooltip-description"
          >
            {{ desc.description }}
          </div>
        </div>
      </UikitTooltip>

      <UikitLabel
        v-if="label"
        :text="label"
        class="osk-products-filters-item-multilist__label"
        small
        bold
      />

      <UikitIcon
        v-if="item.hasMoreValues"
        name="ArrowRightMark"
        class="osk-products-filters-item-multilist__arrow"
      />
    </div>

    <div
      v-if="item.hotValues?.length"
      :class="{
        'osk-products-filters-item-multilist__list': true,
        'osk-products-filters-item-multilist__list_before': isBefore,
        'osk-products-filters-item-multilist__list_after': isAfter,
      }"
    >
      <ul
        ref="$slider"
        class="osk-products-filters-item-multilist__hotItems"
      >
        <li
          v-for="hotItem in item.hotValues"
          :key="hotItem.id"
          :class="{
            'osk-products-filters-item-multilist__hotItem': true,
            'osk-products-filters-item-multilist__hotItem_selected': checkSelected(hotItem),
          }"
          @click="itemClick(hotItem)"
        >
          {{ hotItem.value }}
        </li>
      </ul>
    </div>
  </UikitListItem>
</template>

<script setup lang="ts">
import { MultiListFilter, ListValue, ListHotValue } from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const { $addEvent } = useNuxtApp();

const props = defineProps<{
  modelValue?: ListValue[]
  item: MultiListFilter
  page?: string
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', value: ListValue[]): void,
  (e: 'openFilter', value: MultiListFilter): void,
}>();

const $slider = ref();

const { isMouseMove, isBefore, isAfter } = useOverflowScrollWrapper($slider);

const selected = computed(() => props.item.selectedValues ?? []);

const label = computed(() => {
  if (selected.value?.length < 1) return 0;
  if (selected.value?.length > 1) return selected.value.length;

  const selectedItem = selected.value[0];
  const isHotSelectedItem = !!props.item.hotValues?.find((i) => i.id === selectedItem.id);

  if (isHotSelectedItem) return 1;

  return selectedItem.value;
});

function handleTooltipOpen() {
  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    chapter: `info_${props.item.code}`,
    page: props.page,
  });
}

function checkSelected(hotItem: ListHotValue) {
  return !!selected.value.find((item) => item.id === hotItem.id);
}

function itemClick({ id, value, isSelected }: ListHotValue) {
  if (isMouseMove.value) return;

  let selectedValues: ListValue[];

  if (isSelected) {
    selectedValues = selected.value.filter((i) => i.id !== id);
  } else {
    const isSelectedExist = selected.value.some((s) => s.id === id);

    if (isSelectedExist) return;

    selectedValues = [ ...selected.value, { id, value } ];
  }

  emits('update:modelValue', selectedValues);

  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    chapter: props.item.code,
    // eslint-disable-next-line no-param-reassign
    id_chapter: selected.value.map((val) => val.id).reduce((result, el) => result += `${el};`, '').slice(0, -1),
    id_item: String(id),
    page: props.page,
  });
}

function handleClick() {
  if (!props.item.hasMoreValues) return;
  emits('openFilter', props.item);
}
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-products-filters-item-multilist {
  display: flex;
  flex-direction: column;
  cursor: default;

  &:hover {
    background: transparent !important;
  }

  :deep(.osk-tooltip) {
    margin-left: auto;

    & + .osk-products-filters-item-multilist__label {
      margin-left: $indent-compact;
    }
  }

  &__label {
    margin-left: auto;

    & + .osk-products-filters-item-multilist__arrow {
      margin-left: $indent-compact;
    }
  }

  &__top {
    display: flex;
    align-items: center;
    height: 52px;
    padding: 0 16px;

    &_hover {
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        background: $grey-4;
      }
    }
  }

  &__arrow {
    margin-left: auto;
    margin-right: -5px;
  }

  &__list {
    position: relative;

    &:before {
      content: '';
      width: 30px;
      height: 38px;
      background: linear-gradient(90deg, #fff, rgba(255, 255, 255, 0));
      position: absolute;
      left: 0;
      top: 0;
      opacity: 0;
      visibility: hidden;
      transition: all linear .1s;
    }

    &:after {
      content: '';
      width: 30px;
      height: 38px;
      background: linear-gradient(270deg, #fff, rgba(255, 255, 255, 0));
      position: absolute;
      right: 0;
      top: 0;
      opacity: 0;
      visibility: hidden;
      transition: all linear .1s;
    }

    &_before {
      &:before {
        opacity: 1;
        visibility: visible;
      }
    }

    &_after {
      &:after {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  &__hotItems {
    padding-bottom: $indent-medium;
    display: flex;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__hotItem {
    line-height: 16px;
    padding: 10px 12px;
    border: 0.5px solid $grey-3;
    border-radius: 100px;
    white-space: nowrap;
    cursor: pointer;
    transition: all linear .2s;

    &:not(:last-child) {
      margin-right: 12px;
    }

    &_selected {
      background: $grey-4;
      border-color: $grey-4;
    }

    @include media-query(lg-and-up) {
      &:hover {
        background: $grey-4;
        border-color: $grey-4;
      }
    }
  }

  &__tooltip-list {
    &:not(:last-child) {
      padding-bottom: 32px;
      border-bottom: 0.5px solid #DBDBDB;
      margin-bottom: 12.5px;

      @include media-query(lg-and-up) {
        border: 0;
        margin-bottom: 0;
        padding-bottom: 52px;
      }
    }
  }

  &__tooltip-name {
    font-weight: 700;
    font-size: 16px;
    line-height: 20px;
    margin-bottom: 12px;

    @include media-query(lg-and-up) {
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
      margin-bottom: 20px;
    }
  }

  &__tooltip-description {
    font-size: 14px;
    line-height: normal;
    white-space: pre-wrap;

    @include media-query(lg-and-up) {
      line-height: 20px;
    }
  }
}
</style>
